<template>
  <div>
    <translatable
      v-if="items.length"
      :language="language"
      v-model="object"
      :field-name="fieldName"
    >
      <template
        #default="{ fieldMutator, dynamicFieldValue }"
      >
        <div>
          <template v-for="attr in dynamicFieldValue">
            <component
              :is="itemComponent"
              :value="attr"
              :key="`${attr.key}_${language.code}`"
              :hide-buttons="language && !language.default"
              :language="language"
              :model="value"
              @created="createOrUpdateField"
              @change="val => updateField(val, dynamicFieldValue, fieldMutator)"
              @removed="key => remove(key, fieldMutator)"
              @removedSubField="(mainKey, subKey) => removeSubField(mainKey, subKey)"
            />
          </template>
        </div>
      </template>
    </translatable>
    <v-flex
      v-if="!items.length && !creating"
    >
      {{ $t(noItemsText) }}
    </v-flex>
    <v-flex
      v-if="creating && language && language.default"
      class="pa-0"
    >
      <component
        :is="itemComponent"
        :hide-buttons="language && !language.default"
        :language="language"
        :model="value"
        is-new
        @created="createNewItem"
        @removed="$emit('created')"
      />
    </v-flex>
  </div>
</template>

<script>
import Translatable from '@/components/widgets/Translatable'
import { mapGetters } from 'vuex'

export default {
  name: 'TranslatableCollection',

  components: { Translatable },

  props: {
    language: {
      type: Object,
      required: false,
      default: null
    },
    value: {
      type: Object,
      required: true
    },
    fieldName: {
      type: String,
      required: true
    },
    subfieldName: {
      type: String,
      required: false,
      default: null
    },
    itemComponent: {
      type: [String, Object],
      required: true
    },
    noItemsText: {
      type: String,
      default: 'No items'
    },
    creating: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      object: this.value
    }
  },

  computed: {
    ...mapGetters(['languagesList']),
    items () {
      return this.value[this.fieldName] || []
    }
  },

  created () {
    this.updateTranslations()
    this.$emit('input', this.object)
  },

  methods: {
    updateField (val, dynamicFieldValue, fieldMutator) {
      dynamicFieldValue = dynamicFieldValue.map(prop => {
        if ((val.key && prop.key === val.key) || prop.name === val.name) {
          prop = val
        }

        return prop
      })

      fieldMutator(dynamicFieldValue)
    },

    createOrUpdateField (item) {
      const compare = (left, right) => (left.key && right.key === left.key) || right.name === left.name
      const hasProp = this.object[this.fieldName] && this.object[this.fieldName].some(p => compare(item, p))
      if (!hasProp) {
        this.createNewItem(item)
        return
      }

      this.object[this.fieldName] = this.object[this.fieldName].map(p => {
        if (compare(item, p)) {
          p.key = item.key
        }
        return p
      })
      this.updateTranslations()

      this.$emit('input', this.object)
      this.$emit('created', item)
    },

    createNewItem (prop) {
      const item = { ...prop }

      this.object[this.fieldName].push(item)
      this.updateTranslations()

      this.$emit('input', this.object)
      this.$emit('created', item)
    },

    remove (key, mutator) {
      const element = this.object[this.fieldName].find(i => i.key === key)
      this.object[this.fieldName] = this.object[this.fieldName].filter(i => i.key !== key)
      mutator(this.object[this.fieldName])

      this.languagesList.forEach(lang => {
        if (lang.default) {
          return
        }

        if (!this.object.translations.hasOwnProperty(lang.code) ||
          !this.object.translations[lang.code].hasOwnProperty(this.fieldName)) {
          return
        }

        this.object.translations[lang.code][this.fieldName] = this.object.translations[lang.code][this.fieldName].filter(i => i.key !== key)
      })

      this.$emit('input', this.object)
      this.$emit('removed', element)
    },

    removeSubField (key, subKey) {
      if (!this.subfieldName) {
        return
      }

      this.languagesList.forEach(lang => {
        if (lang.default) {
          return
        }

        if (!this.object.translations.hasOwnProperty(lang.code) ||
          !this.object.translations[lang.code].hasOwnProperty(this.fieldName)
        ) {
          return
        }

        this.object.translations[lang.code][this.fieldName] = this.object.translations[lang.code][this.fieldName].map(field => {
          if (!field[this.subfieldName].some(i => i.key === subKey)) {
            return field
          }

          field[this.subfieldName] = field[this.subfieldName].filter(i => i.key !== subKey)

          return field
        })
      })

      this.$emit('input', this.object)
    },

    updateTranslations () {
      if (!this.object[this.fieldName] || !this.object[this.fieldName].length) {
        return
      }

      this.languagesList.forEach(lang => {
        if (lang.default) {
          return
        }

        if (!this.object.translations) {
          this.object.translations = {}
        }

        if (!this.object.translations.hasOwnProperty(lang.code)) {
          this.object.translations[lang.code] = {}
        }

        if (!this.object.translations[lang.code].hasOwnProperty(this.fieldName)) {
          this.object.translations[lang.code][this.fieldName] = []
        }

        this.object[this.fieldName].forEach(mainProp => {
          const hasProp = this.object.translations[lang.code][this.fieldName].some(prop => prop.key === mainProp.key)
          if (hasProp) {
            return
          }

          if (!mainProp.key) {
            mainProp.key = null
          }

          const newProp = JSON.parse(JSON.stringify(mainProp))
          this.object.translations[lang.code][this.fieldName].push(newProp)
        })
      })
    }
  }
}
</script>
